import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { StatisticProjectControlling } from '@work4all/models/lib/Classes/StatisticProjectControlling.entity';

import { settings, useSetting } from '../../../settings';
import {
  projectControllingActualColumnsZod,
  projectControllingComparisonColumnsZod,
  projectControllingPlannedColumnsZod,
  projectControllingPlannedTimesColumnsZod,
  projectControllingTargetContributionMarginColumnsZod,
} from '../../../settings/settings';

export type ProjectControllingCategory =
  | 'planned'
  | 'actual'
  | 'comparison'
  | 'plannedTimes'
  | 'targetContributionMargin';

type StatisticFieldResolver = (
  data: StatisticProjectControlling | undefined
) => number | null;

export type DisplayFieldKey =
  | keyof z.infer<typeof projectControllingPlannedColumnsZod>
  | keyof z.infer<typeof projectControllingActualColumnsZod>
  | keyof z.infer<typeof projectControllingComparisonColumnsZod>
  | keyof z.infer<typeof projectControllingPlannedTimesColumnsZod>
  | keyof z.infer<typeof projectControllingTargetContributionMarginColumnsZod>;

interface DisplayField {
  label: string;
  resolve: StatisticFieldResolver;
  key: DisplayFieldKey;
  displayOrder?: number;
}

export const useProjectcontrollingFields = (category: string) => {
  const { t } = useTranslation();

  const projectControllingPlannedValuesComesFrom = useSetting(
    settings.projectControllingPlannedValuesComesFrom()
  );

  const targetContributionMarginFields: DisplayField[] = useMemo(
    () => [
      {
        label: t('STATISTIC.PROJECTCONTROLLING.TARGET_CONTRIBUTION_MARGIN'),
        resolve: (item) => {
          const costActual =
            (item?.ownWorkCostsActual ?? 0) +
            (item?.externalWorkCostsActual ?? 0) +
            (item?.proformaIncomingInvoice ?? 0);
          const revenueActual = item?.revenueFromInvoice ?? 0;

          const dbActual = revenueActual
            ? ((revenueActual - costActual) / revenueActual) * 100
            : null;

          return dbActual;
        },
        key: 'targetContributionMargin',
        displayOrder: 0,
      },
    ],
    [t]
  );

  const plannedTimesFields: DisplayField[] = useMemo(
    () => [
      {
        label: t('COMMON.PLANNED_TIMES'),
        resolve: (item) => {
          switch (projectControllingPlannedValuesComesFrom.value) {
            case 'calculation':
              return item?.ownWorkFromEstimationHoursEstimated ?? 0;
            case 'contract':
              return item?.ownWorkFromOrderHoursEstimated ?? 0;
            case 'offer':
            default:
              return item?.ownWorkFromOfferHoursEstimated ?? 0;
          }
        },
        key: 'plannedTimes',
        displayOrder: 0,
      },
    ],
    [projectControllingPlannedValuesComesFrom.value, t]
  );

  const tempPlannedFields: DisplayField[] = useMemo(
    () => [
      {
        label: t('STATISTIC.PROJECTCONTROLLING.COSTS'),
        resolve: (item) => {
          switch (projectControllingPlannedValuesComesFrom.value) {
            case 'calculation':
              return (
                (item?.ownWorkFromEstimationCostsEstimated ?? 0) +
                (item?.externalWorkFromEstimationCostsEstimated ?? 0)
              );
            case 'contract':
              return (
                (item?.ownWorkFromOrderCostsEstimated ?? 0) +
                (item?.externalWorkFromOrderCostsEstimated ?? 0)
              );
            case 'offer':
            default:
              return (
                (item?.ownWorkFromOfferCostsEstimated ?? 0) +
                (item?.externalWorkFromOfferCostsEstimated ?? 0)
              );
          }
        },
        key: 'totalcosts',
        displayOrder: 2,
      },
      {
        label: t('STATISTIC.PROJECTCONTROLLING.CONTRIBUTION_MARGIN'),
        resolve: (item) => {
          let cost = 0;
          let revenue = 0;

          switch (projectControllingPlannedValuesComesFrom.value) {
            case 'calculation':
              cost =
                (item?.ownWorkFromEstimationCostsEstimated ?? 0) +
                (item?.externalWorkFromEstimationCostsEstimated ?? 0);
              revenue = item?.revenueFromEstimation ?? 0;
              break;
            case 'contract':
              cost =
                (item?.ownWorkFromOrderCostsEstimated ?? 0) +
                (item?.externalWorkFromOrderCostsEstimated ?? 0);
              revenue = item?.revenueFromOrder ?? 0;
              break;
            case 'offer':
            default:
              cost =
                (item?.ownWorkFromOfferCostsEstimated ?? 0) +
                (item?.externalWorkFromOfferCostsEstimated ?? 0);
              revenue = item?.revenueFromOffer ?? 0;
              break;
          }

          return revenue - cost;
        },
        key: 'dbinEUR',
        displayOrder: 4,
      },
      {
        label: t('STATISTIC.PROJECTCONTROLLING.dbin%'),
        resolve: (item) => {
          let cost = 0;
          let revenue = 0;

          switch (projectControllingPlannedValuesComesFrom.value) {
            case 'calculation':
              cost =
                (item?.ownWorkFromEstimationCostsEstimated ?? 0) +
                (item?.externalWorkFromEstimationCostsEstimated ?? 0);
              revenue = item?.revenueFromEstimation ?? 0;
              break;
            case 'contract':
              cost =
                (item?.ownWorkFromOrderCostsEstimated ?? 0) +
                (item?.externalWorkFromOrderCostsEstimated ?? 0);
              revenue = item?.revenueFromOrder ?? 0;
              break;
            case 'offer':
            default:
              cost =
                (item?.ownWorkFromOfferCostsEstimated ?? 0) +
                (item?.externalWorkFromOfferCostsEstimated ?? 0);
              revenue = item?.revenueFromOffer ?? 0;
              break;
          }

          return revenue ? ((revenue - cost) / revenue) * 100 : null;
        },
        key: 'dbinPROCENT',
        displayOrder: 5,
      },
    ],
    [projectControllingPlannedValuesComesFrom.value, t]
  );

  switch (projectControllingPlannedValuesComesFrom.value) {
    case 'contract':
      tempPlannedFields.push({
        label: t('STATISTIC.PROJECTCONTROLLING.INTERNAL'),
        resolve: (item) => item?.ownWorkFromOrderCostsEstimated ?? null,
        key: 'internalcontribution',
        displayOrder: 0,
      });
      tempPlannedFields.push({
        label: t('STATISTIC.PROJECTCONTROLLING.EXTERNAL'),
        resolve: (item) => item?.externalWorkFromOrderCostsEstimated ?? null,
        key: 'externalcontribution',
        displayOrder: 1,
      });
      tempPlannedFields.push({
        label: t('STATISTIC.PROJECTCONTROLLING.REVENUE'),
        resolve: (item) => item?.revenueFromOrder ?? null,
        key: 'sales',
        displayOrder: 3,
      });
      break;
    case 'calculation':
      tempPlannedFields.push({
        label: t('STATISTIC.PROJECTCONTROLLING.INTERNAL'),
        resolve: (item) => item?.ownWorkFromEstimationCostsEstimated ?? null,
        key: 'internalcontribution',
        displayOrder: 0,
      });
      tempPlannedFields.push({
        label: t('STATISTIC.PROJECTCONTROLLING.EXTERNAL'),
        resolve: (item) =>
          item?.externalWorkFromEstimationCostsEstimated ?? null,
        key: 'externalcontribution',
        displayOrder: 1,
      });
      tempPlannedFields.push({
        label: t('STATISTIC.PROJECTCONTROLLING.REVENUE'),
        resolve: (item) => item?.revenueFromEstimation ?? null,
        key: 'sales',
        displayOrder: 3,
      });
      break;
    case 'offer':
    default:
      tempPlannedFields.push({
        label: t('STATISTIC.PROJECTCONTROLLING.INTERNAL'),
        resolve: (item) => item?.ownWorkFromOfferCostsEstimated ?? null,
        key: 'internalcontribution',
        displayOrder: 0,
      });
      tempPlannedFields.push({
        label: t('STATISTIC.PROJECTCONTROLLING.EXTERNAL'),
        resolve: (item) => item?.externalWorkFromOfferCostsEstimated ?? null,
        key: 'externalcontribution',
        displayOrder: 1,
      });
      tempPlannedFields.push({
        label: t('STATISTIC.PROJECTCONTROLLING.REVENUE'),
        resolve: (item) => item?.revenueFromOffer ?? null,
        key: 'sales',
        displayOrder: 3,
      });
      break;
  }

  const plannedFields: DisplayField[] = useMemo(
    () =>
      [...tempPlannedFields].sort(
        (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
      ),
    [tempPlannedFields]
  );

  const actualFields: DisplayField[] = [
    {
      label: t('STATISTIC.PROJECTCONTROLLING.internalcontribution'),
      resolve: (item) => item?.ownWorkCostsActual ?? null,
      key: 'internalcontribution',
    },
    {
      label: t('STATISTIC.PROJECTCONTROLLING.externalcontribution'),
      resolve: (item) => item?.externalWorkCostsActual ?? null,
      key: 'externalcontribution',
    },

    {
      label: t('STATISTIC.PROJECTCONTROLLING.costswithoutreceipt'),
      resolve: (item) => item?.proformaIncomingInvoice ?? null,
      key: 'costswithoutreceipt',
    },
    {
      label: t('STATISTIC.PROJECTCONTROLLING.totalcosts'),
      resolve: (item) =>
        (item?.ownWorkCostsActual ?? 0) +
        (item?.externalWorkCostsActual ?? 0) +
        (item?.proformaIncomingInvoice ?? 0),
      key: 'totalcosts',
    },
    {
      label: t('STATISTIC.PROJECTCONTROLLING.sales'),
      resolve: (item) => item?.revenueFromInvoice ?? null,
      key: 'sales',
    },

    {
      label: t('STATISTIC.PROJECTCONTROLLING.dbin€'),
      resolve: (item) => {
        const cost =
          (item?.ownWorkCostsActual ?? 0) +
          (item?.externalWorkCostsActual ?? 0) +
          (item?.proformaIncomingInvoice ?? 0);
        const revenue = item?.revenueFromInvoice ?? 0;
        return revenue - cost;
      },
      key: 'dbinEUR',
    },
    {
      label: t('STATISTIC.PROJECTCONTROLLING.dbin%'),
      resolve: (item) => {
        const cost =
          (item?.ownWorkCostsActual ?? 0) +
          (item?.externalWorkCostsActual ?? 0) +
          (item?.proformaIncomingInvoice ?? 0);
        const revenue = item?.revenueFromInvoice ?? 0;
        return revenue ? ((revenue - cost) / revenue) * 100 : null;
      },
      key: 'dbinPROCENT',
    },
  ];

  const comparisonFields: DisplayField[] = [
    {
      label: t('STATISTIC.PROJECTCONTROLLING.planVsIst'),
      resolve: (item) => {
        const plannedDbCol = plannedFields.find((x) => x.key === 'dbinPROCENT');
        const actualDbCol = actualFields.find((x) => x.key === 'dbinPROCENT');

        const plannedDbValue = plannedDbCol.resolve(item);
        const actualDbValue = actualDbCol.resolve(item);

        return actualDbCol ? actualDbValue - plannedDbValue : null;
      },
      key: 'planVsIst',
    },
  ];

  const fieldsByCategory: Record<ProjectControllingCategory, DisplayField[]> = {
    planned: plannedFields,
    actual: actualFields,
    comparison: comparisonFields,
    plannedTimes: plannedTimesFields,
    targetContributionMargin: targetContributionMarginFields,
  };

  const fields: DisplayField[] = fieldsByCategory[category];
  return fields;
};
